import { useState, useEffect, useCallback } from 'react';
import { Goal } from '../types/goals';
import { getGoalsNeedingReminders, markGoalReminderViewed } from '../services/firebase/goalReminder.service';
import { 
  scheduleGoalReminder, 
  cancelGoalReminder, 
  cancelGoalReminders as cancelNativeReminders
} from '../services/ReminderManager.service';
import { Capacitor } from '@capacitor/core';
import { getAuth } from 'firebase/auth';
import { updateData } from '../services/firebase/api-wrapper.service';
import logger from '../utils/logger';

/**
 * Custom hook for managing goal reminders
 */
const useGoalReminders = () => {
  const [reminders, setReminders] = useState<{ goal: Goal; timestamp: number }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Fetch goal reminders that need to be shown
  const fetchDueReminders = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch goals that need reminders from Firebase service
      const goalsDueForReminders = await getGoalsNeedingReminders();
      
      // Process each goal to create reminder objects
      const now = Date.now();
      const dueReminders = goalsDueForReminders.map(goal => {
        // Use timestamp from reminder settings or now
        const timestamp = goal.reminderSettings?.lastScheduled || now;
        return { goal, timestamp };
      });
      
      setReminders(prev => {
        // Merge with existing reminders, removing duplicates
        const existingIds = new Set(prev.map(r => r.goal.id));
        const newReminders = dueReminders.filter(r => !existingIds.has(r.goal.id));
        return [...prev, ...newReminders];
      });
    } catch (error) {
      logger.error('Error fetching goal reminders:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Schedule a reminder for a specific goal
  const scheduleReminder = useCallback(async (goal: Goal, reminderTime: number) => {
    try {
      // Schedule in the native layer using Capacitor if available
      const { success, id } = await scheduleGoalReminder(
        goal, 
        reminderTime,
        'Goal Reminder',
        `Reminder for your goal: ${goal.name}`
      );
      
      if (!success) {
        throw new Error('Failed to schedule reminder in native layer');
      }
      
      // Update the goal to record the scheduled reminder (RTDB).
      // updateData merges into reminderSettings, preserving sibling fields.
      const user = getAuth().currentUser;
      if (user) {
        await updateData(`goals/private/${user.uid}/${goal.id}/reminderSettings`, {
          lastScheduled: reminderTime,
          notificationId: id || null,
          updatedAt: Date.now()
        });
      }
      
      // Update local state
      setReminders(prev => [
        ...prev,
        { goal, timestamp: reminderTime }
      ]);
      
      // Return success
      return { success: true, id };
    } catch (error) {
      logger.error('Error scheduling goal reminder:', error);
      return { success: false, error };
    }
  }, []);
  
  // Cancel all reminders for a goal
  const cancelReminders = useCallback(async (goalId: string) => {
    try {
      // Find goal in reminders to get notification ID
      const goalReminder = reminders.find(r => r.goal.id === goalId);
      const notificationId = goalReminder?.goal.reminderSettings?.notificationId;
      
      // First cancel in native layer
      let nativeCancelSuccess = false;
      
      if (notificationId) {
        // Cancel specific notification if we have the ID
        nativeCancelSuccess = await cancelGoalReminder(notificationId);
      } else {
        // Otherwise cancel all reminders for this goal
        nativeCancelSuccess = await cancelNativeReminders(goalId);
      }
      
      if (!nativeCancelSuccess) {
        logger.warn(`Native reminder cancellation failed or completed with warnings for goal ${goalId}`);
      }
      
      // Always update the goal to logically disable reminders (RTDB), even if
      // native cancellation had issues. null values clear those child keys.
      const user = getAuth().currentUser;
      if (user) {
        await updateData(`goals/private/${user.uid}/${goalId}/reminderSettings`, {
          enabled: false,
          lastScheduled: null,
          notificationId: null,
          updatedAt: Date.now()
        });
      }
      
      // Remove from local state
      setReminders(prev => prev.filter(r => r.goal.id !== goalId));
      
      // Return success based on Firebase update (more important than native cancellation)
      return { success: true, nativeCancellation: nativeCancelSuccess };
    } catch (error) {
      logger.error('Error cancelling goal reminders:', error);
      
      // Still remove from local state even if Firebase update fails
      setReminders(prev => prev.filter(r => r.goal.id !== goalId));
      
      return { success: false, error };
    }
  }, [reminders]);
  
  // Dismiss a specific reminder
  const dismissReminder = useCallback(async (goalId: string, timestamp: number) => {
    try {
      // Mark as viewed in Firebase service
      await markGoalReminderViewed(goalId);
      
      // Remove from local state
      setReminders(prev => 
        prev.filter(r => !(r.goal.id === goalId && r.timestamp === timestamp))
      );
      
      // Return success
      return { success: true };
    } catch (error) {
      logger.error('Error dismissing goal reminder:', error);
      
      // Still remove from local state even if Firebase update fails
      setReminders(prev => 
        prev.filter(r => !(r.goal.id === goalId && r.timestamp === timestamp))
      );
      
      return { success: false, error };
    }
  }, []);
  
  // Fetch reminders on mount and periodically
  useEffect(() => {
    fetchDueReminders();
    
    // Set up a refresh timer to check for new reminders
    // Every minute for active app, more frequent on mobile
    const refreshInterval = setInterval(() => {
      fetchDueReminders();
    }, Capacitor.isNativePlatform() ? 30000 : 60000); // 30s on mobile, 60s on web
    
    return () => clearInterval(refreshInterval);
  }, [fetchDueReminders]);
  
  return {
    reminders,
    isLoading,
    refreshReminders: fetchDueReminders,
    scheduleReminder,
    cancelReminders,
    dismissReminder
  };
};

export default useGoalReminders;